import { API } from '@/core/Api';
import { NodeItem } from '@/views/flow/nodeview/NodeItem';
import { VideoModel } from '../VideoEngineer';

// 텍스트를 비디오로 변환하는 AI 서비스
export class TextToVideoAI {
  airflowEnpoint = 'airflow';
  backgroundEndpoint = 'generate/background';
  /**
   * Hailuo 02 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateWithHailuo(
    prompt: string,
    options: {
      prompt_optimizer?: boolean; // 프롬프트 최적화 사용 여부
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      prompt_optimizer: options.prompt_optimizer ?? true,
    };

    const response = await API.post('/api/ai/text-to-video/hailuo-02-pro', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '비디오 생성에 실패했습니다.');
    }
  }

  /**
   * 이미지를 비디오로 변환하는 Hailuo 02 Pro
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateImageToVideoWithHailuo(
    prompt: string,
    imageUrl: string,
    options: {
      prompt_optimizer?: boolean;
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      image_url: imageUrl,
      prompt_optimizer: options.prompt_optimizer ?? true,
    };

    const response = await API.post('/api/ai/image-to-video/hailuo-02-pro', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지를 비디오로 변환하는데 실패했습니다.');
    }
  }

  /**
   * Runway Gen4 Turbo를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateWithRunwayGen4(
    prompt: string,
    imageUrl: string,
    options: {
      duration?: number; // 비디오 길이 (초, 기본값: 5)
      ratio?: string; // 화면 비율 (예: "1280:720", "960:960")
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      model: 'gen4_turbo',
      prompt_text: prompt,
      prompt_image: imageUrl,
      duration: options.duration || 5,
      ratio: options.ratio || '1280:720',
    };

    const response = await API.post('/api/ai/image-to-video/runway-gen4-turbo', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Runway 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Vidu Reference를 사용하여 참조 이미지들로 비디오 생성
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param referenceImageUrls 참조할 이미지 URL들 (최대 4개)
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateWithViduReference(
    prompt: string,
    referenceImageUrls: string[],
    options: {
      aspect_ratio?: string; // 화면 비율 (예: "16:9", "1:1")
      seed?: number; // 시드 값 (일관성을 위해)
      signal?: AbortSignal;
    } = {},
  ) {
    if (referenceImageUrls.length > 4) {
      throw new Error('참조 이미지는 최대 4개까지 사용할 수 있습니다.');
    }

    const requestData = {
      prompt,
      reference_image_urls: referenceImageUrls,
      aspect_ratio: options.aspect_ratio || '16:9',
      seed: options.seed,
    };

    const response = await API.post('/api/ai/image-to-video/vidu-reference', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Vidu 비디오 생성에 실패했습니다.');
    }
  }

  async airflowGenerationWithRunWayGen3(
    prompt: string,
    node: NodeItem,
    options: {
      duration?: number; // 비디오 길이 (초, 기본값: 5)
      ratio?: string; // 화면 비율 (예: "1280:720", "960:960")
    } = {},
  ) {
    const requestData = {
      reqBody: {
        model: 'veo3.1_fast',
        prompt_text: prompt,
        duration: options.duration || 6,
        ratio: options.ratio || '1280:720',
        // 1. ratio: "1280:720", "720:1280", "1080:1920", "1920:1080" 만 지원
        // 2. duration: 4, 6, 8 만 지원 (5, 10은 안 됨)
      },
      info: {
        node_id: node.id,
        model: 'runway-veo31-fast',
        model_name: VideoModel.RUNWAY_VEO_FAST, //for frontend
      },
    };

    const response = await API.post(`/api/ai/text-to-video/${this.airflowEnpoint}`, requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Runway 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Hailuo 02 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @param node 업데이트 할 노드
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateWithHailuo(
    prompt: string,
    node: NodeItem,
    options: {
      prompt_optimizer?: boolean; // 프롬프트 최적화 사용 여부
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        prompt_optimizer: options.prompt_optimizer ?? true,
      },
      info: {
        model: 'hailuo-02-pro',
        node_id: node.id,
        model_name: VideoModel.HAILUO_IMAGE_TO_VIDEO,
      },
    };
    console.log(requestData);
    const response = await API.post(`/api/ai/text-to-video/${this.airflowEnpoint}`, requestData);
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Seedance v1 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @param node 업데이트 할 노드
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateWithSeedance(
    prompt: string,
    node: NodeItem,
    options: {
      aspect_ratio?: string; // 화면 비율 (21:9, 16:9, 4:3, 1:1, 3:4, 9:16)
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        aspect_ratio: options.aspect_ratio || '16:9',
        resolution: options.resolution || '720p',
        duration: options.duration || '5',
        camera_fixed: options.camera_fixed ?? false,
        seed: options.seed,
        enable_safety_checker: options.enable_safety_checker ?? true,
      },
      info: {
        model: 'seedance-pro',
        node_id: node.id,
        model_name: VideoModel.SEEDANCE,
      },
    };
    console.log(requestData);
    const response = await API.post(`/api/ai/text-to-video/${this.airflowEnpoint}`, requestData);
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Seedance v1 Pro를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @param node
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateImageToVideoWithSeedance(
    prompt: string,
    node: NodeItem,
    imageUrl: string,
    options: {
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
      aspect_ratio?: string; // "21:9", "16:9", "4:3", "1:1", "3:4", "9:16"
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        image_url: imageUrl,
        resolution: options.resolution || '720p',
        duration: options.duration || '5',
        camera_fixed: options.camera_fixed ?? false,
        seed: options.seed,
        enable_safety_checker: options.enable_safety_checker ?? true,
        aspect_ratio: options.aspect_ratio || '16:9',
      },
      info: {
        model: 'seedance-pro',
        node_id: node.id,
        model_name: VideoModel.SEEDANCE,
      },
    };

    const response = await API.post(`/api/ai/image-to-video/${this.airflowEnpoint}`, requestData);
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 이미지-비디오 변환에 실패했습니다.');
    }
  }

  /**
   * 이미지를 비디오로 변환하는 Hailuo 02 Pro
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imgNode 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateImageToVideoWithHailuo(
    prompt: string,
    node: NodeItem,
    img: string,
    options: {
      prompt_optimizer?: boolean;
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        image_url: img,
        prompt_optimizer: options.prompt_optimizer ?? true,
      },
      info: {
        model: 'hailuo-02-pro',
        node_id: node.id,
        model_name: VideoModel.HAILUO_IMAGE_TO_VIDEO,
      },
    };

    const response = await API.post(`/api/ai/image-to-video/${this.airflowEnpoint}`, requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지를 비디오로 변환하는데 실패했습니다.');
    }
  }

  /**
   * Runway Gen4 Turbo를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imgNode 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateWithRunwayGen4(
    prompt: string,
    node: NodeItem,
    img: string,
    options: {
      duration?: number; // 비디오 길이 (초, 기본값: 5)
      ratio?: string; // 화면 비율 (예: "1280:720", "960:960")
    } = {},
  ) {
    const requestData = {
      reqBody: {
        model: 'gen4_turbo',
        prompt_text: prompt,
        prompt_image: img,
        duration: options.duration || 5,
        ratio: options.ratio || '1280:720', //"1280:720" "720:1280" "1104:832" "832:1104" "960:960" "1584:672" "1280:768" "768:1280"
      },
      info: {
        node_id: node.id,
        model: 'runway-gen4-turbo',
        model_name: VideoModel.RUNWAY_GEN4_TURBO, //for frontend
      },
    };

    const response = await API.post(`/api/ai/image-to-video/${this.airflowEnpoint}`, requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Runway 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Vidu Reference를 사용하여 참조 이미지들로 비디오 생성
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param referenceImageUrls 참조할 이미지 URL들 (최대 4개)
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async airflowGenerateWithViduReference(
    prompt: string,
    node: NodeItem,
    referenceImageUrls: string[],
    options: {
      aspect_ratio?: string; // 화면 비율 (예: "16:9", "1:1")
      seed?: number; // 시드 값 (일관성을 위해)
    } = {},
  ) {
    // console.log('임시로 이미지값 고정');
    if (referenceImageUrls.length > 4) {
      throw new Error('참조 이미지는 최대 4개까지 사용할 수 있습니다.');
    }

    const requestData = {
      reqBody: {
        model: 'vidu-reference',
        prompt,
        reference_image_urls: referenceImageUrls,
        // reference_image_urls: ['https://maestro.k8s.sys-flow.com/images/temp/image-0.png'],
        aspect_ratio: options.aspect_ratio || '16:9',
        seed: options.seed,
      },
      info: {
        node_id: node.id,
        model: 'vidu-reference',
        model_name: VideoModel.VIDU_REFERENCE,
      },
    };

    const response = await API.post(`/api/ai/image-to-video/${this.airflowEnpoint}`, requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Vidu 비디오 생성에 실패했습니다.');
    }
  }

  async backgroundGenerationWithRunWayGen3(
    prompt: string,
    node: NodeItem,
    options: {
      duration?: number; // 비디오 길이 (4,6,8)
      ratio?: string; // 화면 비율 "1280:720"
      // '720:1280';
      // '1080:1920';
      // '1920:1080';
      prompt_image?: string;
    } = {},
  ) {
    const requestData: any = {
      reqBody: {
        model: 'veo3.1_fast',
        prompt_text: prompt,
        duration: options.duration || 6,
        ratio: options.ratio || '1280:720',
        // 1. ratio: "1280:720", "720:1280", "1080:1920", "1920:1080" 만 지원
        // 2. duration: 4, 6, 8 만 지원 (5, 10은 안 됨)
      },
      info: {
        node_id: node.id,
        model: 'runway-veo31-fast',
        model_name: VideoModel.RUNWAY_VEO_FAST, //for frontend
      },
    };

    if (options.prompt_image) {
      requestData['reqBody']['prompt_image'] = options.prompt_image;
    }
    const response = await API.post(
      `/api/ai/text-to-video/${this.backgroundEndpoint}`,
      requestData,
    );

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Runway 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Hailuo 02 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @param node 업데이트 할 노드
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateWithHailuo(
    prompt: string,
    node: NodeItem,
    options: {
      prompt_optimizer?: boolean; // 프롬프트 최적화 사용 여부
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        prompt_optimizer: options.prompt_optimizer ?? true,
      },
      info: {
        model: 'hailuo-02-pro',
        node_id: node.id,
        model_name: VideoModel.HAILUO_IMAGE_TO_VIDEO,
      },
    };
    console.log(requestData);
    const response = await API.post(
      `/api/ai/text-to-video/${this.backgroundEndpoint}`,
      requestData,
    );
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Seedance v1 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @param node 업데이트 할 노드
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateWithSeedance(
    prompt: string,
    node: NodeItem,
    options: {
      aspect_ratio?: string; // 화면 비율 (21:9, 16:9, 4:3, 1:1, 3:4, 9:16)
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        aspect_ratio: options.aspect_ratio || '16:9',
        resolution: options.resolution || '720p',
        duration: options.duration || '5',
        camera_fixed: options.camera_fixed ?? false,
        seed: options.seed,
        enable_safety_checker: options.enable_safety_checker ?? true,
      },
      info: {
        model: 'seedance-pro',
        node_id: node.id,
        model_name: VideoModel.SEEDANCE,
      },
    };
    console.log(requestData);
    const response = await API.post(
      `/api/ai/text-to-video/${this.backgroundEndpoint}`,
      requestData,
    );
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Seedance v1 Pro를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @param node
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateImageToVideoWithSeedance(
    prompt: string,
    node: NodeItem,
    imageUrl: string,
    options: {
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
      aspect_ratio?: string; // "21:9", "16:9", "4:3", "1:1", "3:4", "9:16"
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        image_url: imageUrl,
        resolution: options.resolution || '720p',
        duration: options.duration || '5',
        camera_fixed: options.camera_fixed ?? false,
        seed: options.seed,
        enable_safety_checker: options.enable_safety_checker ?? true,
        aspect_ratio: options.aspect_ratio || '16:9',
      },
      info: {
        model: 'seedance-image-pro',
        node_id: node.id,
        model_name: VideoModel.SEEDANCE,
      },
    };

    const response = await API.post(
      `/api/ai/image-to-video/${this.backgroundEndpoint}`,
      requestData,
    );
    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 이미지-비디오 변환에 실패했습니다.');
    }
  }

  /**
   * 이미지를 비디오로 변환하는 Hailuo 02 Pro
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imgNode 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateImageToVideoWithHailuo(
    prompt: string,
    node: NodeItem,
    img: string,
    options: {
      prompt_optimizer?: boolean;
    } = {},
  ) {
    const requestData = {
      reqBody: {
        prompt,
        image_url: img,
        prompt_optimizer: options.prompt_optimizer ?? true,
      },
      info: {
        model: 'hailuo-image-02-pro',
        node_id: node.id,
        model_name: VideoModel.HAILUO_IMAGE_TO_VIDEO,
      },
    };

    const response = await API.post(
      `/api/ai/image-to-video/${this.backgroundEndpoint}`,
      requestData,
    );

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지를 비디오로 변환하는데 실패했습니다.');
    }
  }

  /**
   * Runway Gen4 Turbo를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imgNode 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateWithRunwayGen4(
    prompt: string,
    node: NodeItem,
    img: string,
    options: {
      duration?: number; // 비디오 길이 (초, 기본값: 5)
      ratio?: string; // 화면 비율 (예: "1280:720", "960:960")
    } = {},
  ) {
    const requestData = {
      reqBody: {
        model: 'gen4_turbo',
        prompt_text: prompt,
        prompt_image: img,
        duration: options.duration || 5,
        ratio: options.ratio || '1280:720', //"1280:720" "720:1280" "1104:832" "832:1104" "960:960" "1584:672" "1280:768" "768:1280"
      },
      info: {
        node_id: node.id,
        model: 'runway-gen4-turbo',
        model_name: VideoModel.RUNWAY_GEN4_TURBO, //for frontend
      },
    };

    const response = await API.post(
      `/api/ai/image-to-video/${this.backgroundEndpoint}`,
      requestData,
    );

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Runway 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Vidu Reference를 사용하여 참조 이미지들로 비디오 생성
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param referenceImageUrls 참조할 이미지 URL들 (최대 4개)
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async backgroundGenerateWithViduReference(
    prompt: string,
    node: NodeItem,
    referenceImageUrls: string[],
    options: {
      aspect_ratio?: string; // 화면 비율 (예: "16:9", "1:1")
      seed?: number; // 시드 값 (일관성을 위해)
    } = {},
  ) {
    // console.log('임시로 이미지값 고정');
    if (referenceImageUrls.length > 4) {
      throw new Error('참조 이미지는 최대 4개까지 사용할 수 있습니다.');
    }

    const requestData = {
      reqBody: {
        model: 'vidu-reference',
        prompt,
        reference_image_urls: referenceImageUrls,
        // reference_image_urls: ['https://maestro.k8s.sys-flow.com/images/temp/image-0.png'],
        aspect_ratio: options.aspect_ratio || '16:9',
        seed: options.seed,
      },
      info: {
        node_id: node.id,
        model: 'vidu-reference',
        model_name: VideoModel.VIDU_REFERENCE,
      },
    };

    const response = await API.post(
      `/api/ai/image-to-video/${this.backgroundEndpoint}`,
      requestData,
    );

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Vidu 비디오 생성에 실패했습니다.');
    }
  }
  /**
   * 사용 가능한 비디오 지속 시간들 (Runway용)
   */
  getAvailableRunwayDurations() {
    return [5, 10]; // 초 단위
  }

  /**
   * 사용 가능한 비디오 화면 비율들 (Runway Gen4 지원)
   */
  getAvailableVideoRatios() {
    return [
      '1280:720', // LANDSCAPE_1280_720
      '1584:672', // LANDSCAPE_1584_672
      '1104:832', // LANDSCAPE_1104_832
      '720:1280', // PORTRAIT_720_1280
      '832:1104', // PORTRAIT_832_1104
      '960:960', // SQUARE_960_960
    ];
  }

  /**
   * 비디오 생성용 예제 프롬프트들
   */
  getExampleVideoPrompts() {
    return [
      'Gentle ocean waves rolling onto a peaceful beach at sunset',
      'Night city traffic with flowing car lights creating light trails',
      'Forest trees gently swaying in the wind, leaves rustling',
      'Time-lapse of clouds slowly moving across a blue sky',
      'Camera slowly rotating around a beautiful garden in bloom',
      'Person turning head with a natural smile, soft lighting',
    ];
  }

  /**
   * Seedance v1 Pro를 사용하여 텍스트를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateWithSeedance(
    prompt: string,
    options: {
      aspect_ratio?: string; // 화면 비율 (21:9, 16:9, 4:3, 1:1, 3:4, 9:16)
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      aspect_ratio: options.aspect_ratio || '16:9',
      resolution: options.resolution || '720p',
      duration: options.duration || '5',
      camera_fixed: options.camera_fixed ?? false,
      seed: options.seed,
      enable_safety_checker: options.enable_safety_checker ?? true,
    };

    const response = await API.post('/api/ai/text-to-video/seedance-pro', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 비디오 생성에 실패했습니다.');
    }
  }

  /**
   * Seedance v1 Pro를 사용하여 이미지를 비디오로 변환
   * @param prompt 비디오 생성을 위한 텍스트 프롬프트
   * @param imageUrl 기반이 될 이미지 URL
   * @param options 추가 옵션들
   * @returns 생성된 비디오 정보
   */
  async generateImageToVideoWithSeedance(
    prompt: string,
    imageUrl: string,
    options: {
      resolution?: string; // 해상도 (480p, 720p, 1080p)
      duration?: string; // 길이 (3-12초)
      camera_fixed?: boolean; // 카메라 고정 여부
      seed?: number; // 재현성을 위한 시드 값
      enable_safety_checker?: boolean; // 안전성 검사 활성화
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      image_url: imageUrl,
      resolution: options.resolution || '720p',
      duration: options.duration || '5',
      camera_fixed: options.camera_fixed ?? false,
      seed: options.seed,
      enable_safety_checker: options.enable_safety_checker ?? true,
    };

    const response = await API.post('/api/ai/image-to-video/seedance-pro', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Seedance 이미지-비디오 변환에 실패했습니다.');
    }
  }

  /**
   * Seedance 지원 종횡비 목록
   */
  getSeedanceAspectRatios() {
    return [
      { value: '21:9', label: '시네마틱 (21:9)' },
      { value: '16:9', label: '와이드스크린 (16:9)' },
      { value: '4:3', label: '전통적 (4:3)' },
      { value: '1:1', label: '정사각형 (1:1)' },
      { value: '3:4', label: '세로형 (3:4)' },
      { value: '9:16', label: '모바일 세로 (9:16)' },
    ];
  }

  /**
   * Seedance 지원 해상도 목록
   */
  getSeedanceResolutions() {
    return [
      { value: '480p', label: '480p (빠름)' },
      { value: '720p', label: '720p (보통)' },
      { value: '1080p', label: '1080p (고품질)' },
    ];
  }

  /**
   * Seedance 지원 길이 옵션들
   */
  getSeedanceDurations() {
    return [
      { value: '3', label: '3초 (빠름)' },
      { value: '5', label: '5초 (권장)' },
      { value: '8', label: '8초 (긴 편)' },
      { value: '12', label: '12초 (최대)' },
    ];
  }

  /**
   * 이미지-비디오 변환용 예제 프롬프트들
   */
  getExampleImageToVideoPrompts() {
    return [
      'Person slowly turning head with a gentle smile',
      'Camera gradually zooming in to focus on the subject',
      'Natural breathing with subtle facial movements',
      'Background elements moving softly to add life',
      'Lighting naturally changing to create atmosphere',
    ];
  }

  /**
   * Seedance용 예제 프롬프트들
   */
  getExampleSeedancePrompts() {
    return [
      'A peaceful lake at sunset with gentle ripples in the water',
      'Majestic eagle soaring through crystal-clear blue sky',
      'Forest trees swaying gently in a soft breeze',
      'Time-lapse of clouds drifting across mountain peaks',
      'Cinematic camera movement through a magical forest',
      'Ocean waves slowly rolling onto a pristine beach',
    ];
  }
}
